// Reputation scale conversion — contract basis points → UI percentage.
//
// `ReputationLedger.sol` stores every axis as a uint16 in basis points:
// DEFAULT_SCORE = 5000 and MAX_SCORE = 10000 mean 50.00 and 100.00. The UI
// treats the number as a literal percentage (`width: ${value}%`, and the text
// inside the ring), so handing it the raw value renders "5000" in an 80px ring
// and four bars 5000% wide.
//
// The contracts are frozen, so the conversion belongs here, at the client
// boundary — the same place `formatUSDC` converts 6-decimal token integers.

/** `ReputationLedger.MAX_SCORE` — the contract's ceiling, 100.00. */
export const REPUTATION_MAX_SCORE = 10000;

/** `ReputationLedger.DEFAULT_SCORE` — seeded on registration, 50.00. */
export const REPUTATION_DEFAULT_SCORE = 5000;

/**
 * Convert one raw contract axis (0–10000) to a UI percentage (0–100).
 *
 * Kept to two decimals because that is exactly the precision the contract
 * encodes; rounding further is a display concern and belongs in the component,
 * not here, so an axis never silently loses resolution on the way up.
 *
 * Clamped to the contract's own range: the ledger cannot exceed MAX_SCORE, so
 * an out-of-range value means a bad read, and a bar wider than its track is a
 * worse answer than a full one.
 */
export function formatReputationScore(raw: number | bigint): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  const clamped = Math.min(Math.max(n, 0), REPUTATION_MAX_SCORE);
  return Math.round(clamped) / 100;
}
